import React, { memo } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { DAY_VARIANTS } from "./constants";
// eslint-disable-next-line import/no-cycle
import { DAY_MARGIN } from "./styled";

const edgeStyles = css`
  background-color: var(--dui-color-gray-200);
  content: "";
  display: block;
  height: 100%;
  position: absolute;
  top: 0;
  z-index: -1;
`;

const hoverStyles = css`
  &:hover {
    border-radius: var(--dui-size-radius-md);
    &::before,
    &::after {
      background-color: var(--dui-color-gray-200);
    }
  }
`;

const selectedStyles = css`
  background-color: var(--dui-color-red-400);
  color: var(--dui-color-white-500);

  &:focus,
  &:hover,
  &:active {
    box-shadow: 0 0 0 2px white, 0 0 0 4px var(--dui-color-red-400);
  }
`;

const variants = {
  [DAY_VARIANTS.TODAY]: css`
    background-color: var(--dui-color-black-500);
    color: var(--dui-color-white-500);
  `,
  [DAY_VARIANTS.SELECTED]: selectedStyles,
  [DAY_VARIANTS.START]: css`
    ${selectedStyles}
    &::after {
      ${edgeStyles}
      right: -${DAY_MARGIN};
      width: calc(10px + ${DAY_MARGIN});
    }
    ${hoverStyles}
  `,
  [DAY_VARIANTS.END]: css`
    ${selectedStyles}
    &::before {
      ${edgeStyles}
      left: -${DAY_MARGIN};
      width: calc(10px + ${DAY_MARGIN});
    }
    ${hoverStyles}
  `,
  [DAY_VARIANTS.BETWEEN]: css`
    background-color: var(--dui-color-gray-200);
    border-radius: 0;
    &::before {
      ${edgeStyles}
      left: -${DAY_MARGIN};
      width: calc(100% + (2 * ${DAY_MARGIN}));
    }
    ${hoverStyles}
    &:focus,
    &:hover,
    &:active {
      border-radius: var(--dui-size-radius-md);
    }
  `,
};

const StyledDay = styled.button`
  all: unset;
  appearance: none;
  background: transparent;
  border: var(--dui-size-space-0);
  border-radius: var(--dui-size-radius-md);
  color: var(--dui-color-black-500);
  cursor: pointer;
  display: inline-block;
  font-family: var(--dui-font-family);
  font-size: var(--dui-size-font-md);
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  height: calc(
    var(--dui-size-space-24x) - (2 * calc(var(--dui-size-theme) * 0.1875rem))
  );
  line-height: var(--dui-size-line-height-lg);
  padding: var(--dui-size-space-0) var(--dui-size-space-0)
    var(--dui-size-space-1);
  position: relative;
  text-align: center;
  vertical-align: middle;
  width: calc(
    var(--dui-size-space-24x) - (2 * calc(var(--dui-size-theme) * 0.1875rem))
  );
  margin: calc(var(--dui-size-theme) * 0.1875rem);

  &[aria-pressed="true"],
  &:focus {
    background: var(--dui-color-red-500);
    box-shadow: none;
    color: var(--dui-color-white-500);
    outline: 0;
  }

  &:hover {
    position: relative;

    &::after {
      content: "";
      display: block;
      position: absolute;
      top: var(--dui-size-space-0);
      left: var(--dui-size-space-0);
      width: var(--dui-size-space-full);
      height: var(--dui-size-space-full);
      box-shadow: var(--dui-size-space-0) var(--dui-size-space-0)
          var(--dui-size-space-0) var(--dui-size-space-1x)
          var(--dui-color-white-500),
        var(--dui-size-space-0) var(--dui-size-space-0) var(--dui-size-space-0)
          calc(var(--dui-size-theme) * 0.1875rem) var(--dui-color-black-500);
      z-index: 2;
      border-radius: var(--dui-size-radius-md);
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;

    &:hover {
      box-shadow: none;
    }
  }

  ${({ variant }) => variants[variant]}
`;

export const Day = memo(
  ({
    day,
    disabled,
    variant,
    onClick,
    onMouseEnter,
    onFocus,
    dataTestid,
    ariaLabel,
  }) => (
    <StyledDay
      data-testid={dataTestid}
      disabled={disabled}
      variant={variant}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onFocus={onFocus}
      type="button"
      role="button"
      aria-label={ariaLabel}
      aria-pressed={variant === DAY_VARIANTS.SELECTED ? "true" : undefined}
      aria-current={variant === DAY_VARIANTS.TODAY ? "date" : undefined}
      aria-selected={variant === DAY_VARIANTS.SELECTED ? "true" : undefined}
    >
      {day}
    </StyledDay>
  )
);

Day.displayName = "Day";

Day.propTypes = {
  dataTestid: PropTypes.string,
  ariaLabel: PropTypes.string,
  /** Day text that will be displayed */
  day: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** What is the variant (today, ) */
  variant: PropTypes.oneOf(Object.keys(variants)),
  disabled: PropTypes.bool,
  /** Onclick handler */
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onFocus: PropTypes.func,
};

Day.defaultProps = {};
